<template lang="pug">
Transition(
  name="FcwSheet__transition"
  :duration="200"
  @after-enter="emit('open')"
  @after-leave="emit('close')"
)
  .FcwSheet(
    v-if="modelValue"
    :class="classes"
    :style="style"
  )
    .FcwSheet__header
      FSkeleton.FcwSheet__title(
        v-if="loading && !title"
        width="90%"
      )
      .FcwSheet__title(v-else)
        h4(v-if="title") {{ title }}

      .FcwSheet__subtitle__loader(v-if="loading")
        FSkeleton(
          width="96px"
          rounded
          height="24px"
        )
      .FcwSheet__subtitle(v-else)
        slot(
          v-if="$slots['subtitle']"
          name="subtitle"
        )
        caption(v-else-if="subtitle") {{ subtitle }}
    .FcwSheet__content
      slot(name="content")
    FButton.FcwSheet__closeButton(
      ghost
      icon
      size="small"
      @click="isOpen = false"
    )
      FIcon(name="close")
</template>

<style lang="stylus">
.FcwSheet
  position fixed
  top 0
  bottom 0
  background var(--color--neutral--light-5)
  width var(--FcwSheet--width)
  padding rem(24)
  height 100%
  overflow hidden
  display flex
  flex-direction column
  elevation-light(4)

  &--floating
    border-radius rem(24)
    margin rem(12)
    border-radius rem(24)
    height 'calc(100% - %s)' % rem(24)

  &--right
    right 0

  +media-down('sm')
    margin rem(0)
    border-radius 0
    width 100%
    height 100%

.FcwSheet__header
  margin-bottom rem(16)

.FcwSheet__title
  position relative
  margin-right rem(32)

.FcwSheet__subtitle__loader
  display flex
  gap rem(8)

.FcwSheet__subtitle,
.FcwSheet__subtitle__loader
  margin-top rem(8)

.FcwSheet__closeButton
  position absolute
  right rem(16)
  top rem(16)
  z-index 10

.FcwSheet__content
  overflow auto
  overflow-x hidden
  height 100%

.FcwSheet__transition-enter-from,
.FcwSheet__transition-leave-to
  transform 'translateX(calc(-100% - %s))' % rem(24)

  &.FcwSheet--right
    transform 'translateX(calc(100% + %s))' % rem(24)

.FcwSheet__transition-enter-active,
.FcwSheet__transition-leave-active
  transition transform 0.3s var(--transition--ease-out)

.FcwSheet__transition-enter-to,
.FcwSheet__transition-leave-from
  transform translateX(0)

  &.FcwSheet--right
    transform translateX(0)
</style>

<script setup lang="ts">
import { genSize } from '@fifteen/design-system-vue';

export interface FcwSheetProps {
  /**
   * Whether the sheet is open
   */
  modelValue?: boolean;
  /**
   * Position of the sheet
   */
  position?: 'left' | 'right';
  /**
   * Title of the sheet
   */
  title?: string;
  /**
   * Subtitle of the sheet
   */
  subtitle?: string;
  /**
   * Width of the sheet
   */
  width?: string | number;
  /**
   * Loading state of the sheet
   */
  loading?: boolean;
  /**
   * Style of the sheet
   */
  type?: 'floating' | 'fixed';
}

const props = withDefaults(defineProps<FcwSheetProps>(), {
  modelValue: false,
  title: '',
  subtitle: '',
  width: '360px',
  loading: false,
  position: 'left',
  type: 'fixed',
});

const isOpen = useVModelProxy({ props });

const emit = defineEmits<{
  (name: 'open'): void;
  (name: 'close'): void;
}>();

const classes = computed<VueClasses>(() => ({
  'FcwSheet--right': props.position === 'right',
  'FcwSheet--floating': props.type === 'floating',
}));

const style = computed(() => ({
  '--FcwSheet--width': genSize(props.width),
}));
</script>
